import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  withStyles,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';

import {
  Print,
  ExpandMore,
  Delete,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from '@material-ui/icons';
import IconButton from 'components/IconButton';
import IconTooltip from 'components/IconTooltip';
import NoContentMessage from 'components/NoContentMessage';
import { uploaderPdfToIframe } from 'utilities/printUtilities';

const getPageRangesForPrintJob = (pages, pageNumbers) => {
  const result = new Map();

  if (!pageNumbers?.length) {
    return result;
  }

  const pageRanges = pageNumbers.reduce(
    (pageRange, item) => {
      const lastArray = pageRange[pageRange.length - 1];
      const lastItem = lastArray[lastArray.length - 1];

      if (!lastItem || item - lastItem === 1) {
        lastArray.push(item);
      } else {
        pageRange.push([item]);
      }

      if (pageNumbers.length === lastArray.length) {
        return lastArray;
      } else {
        return pageRange;
      }
    },
    [[]],
  );

  const getLabelForPage = (pageNumberLabel) =>
    pages.find((page) => page?.index === pageNumberLabel - 1)?.label;

  const setResult = (rangeArray) => {
    const firstPageLabel = getLabelForPage(rangeArray[0]);
    const lastPageLabel = getLabelForPage(rangeArray[rangeArray.length - 1]);

    result.set(`${firstPageLabel} - ${lastPageLabel}`, rangeArray.length);
  };

  if (
    pageRanges.every((page) => pageNumbers.includes(page)) &&
    pageNumbers.every((page) => pageRanges.includes(page))
  ) {
    setResult(pageRanges);
  } else {
    pageRanges.forEach((range) => {
      setResult(range);
    });
  }

  return result;
};

const getPrintPercentForPrintPages = (numberOfPages, totalPages) => {
  if (!numberOfPages || !totalPages) {
    return null;
  }

  const pagesPercent = (numberOfPages / totalPages) * 100;
  return pagesPercent.toFixed(1);
};

const sortPrintJobs = (printJobsList) => {
  return printJobsList
    .filter((printJob) => printJob?.pages?.length > 0)
    .sort((a, b) => {
      // If both print jobs start from the same page, return the shorter one first
      if (a.pageNumbers[0] === b.pageNumbers[0]) {
        return (
          a.pageNumbers[a.pageNumbers.length - 1] -
          b.pageNumbers[b.pageNumbers.length - 1]
        );
      }
      return a.pageNumbers[0] - b.pageNumbers[0];
    });
};

const getReadableText = (printJob, pageRanges) => {
  let displayText = '';
  let readableText = '';

  if (pageRanges.size === 1) {
    displayText = pageRanges.entries().next().value[0];
  } else if (pageRanges.size > 1) {
    displayText = 'Multiple';

    const formatter = new Intl.ListFormat('en', {
      style: 'long',
      type: 'conjunction',
    });
    readableText = formatter.format([...pageRanges.keys()]);
  } else {
    displayText = printJob?.displayText;
  }

  readableText = (
    displayText === 'Multiple' ? readableText : displayText
  ).replaceAll('-', 'to');

  const shouldTranslate = displayText === 'Multiple' ? true : false;

  return { displayText, readableText, shouldTranslate };
};

const useStyles = makeStyles((theme) => {
  return {
    root: {
      marginTop: theme.spacing(3),
    },
    actionIconButton: {
      color: theme.palette.greyBlue[600],
    },
    actionCell: {
      width: '48px',
      padding: '0',
    },
    accordionSummaryRoot: {
      backgroundColor: 'rgba(0, 0, 0, .03)',
      border: '1px solid rgba(0, 0, 0, .125)',
      borderRadius: theme.spacing(0.5),
      marginBottom: theme.spacing(-0.125),
      minHeight: theme.spacing(5.375),
      '&.Mui-expanded': {
        minHeight: theme.spacing(5.375),
      },
    },
    accordionSummaryContent: {
      display: 'flex',
      alignItems: 'center',
      margin: 0,
      '&.Mui-expanded': {
        margin: 0,
      },
    },
    accordionIcon: {
      padding: theme.spacing(1.25, 1.5),
    },
    accordionTitle: {
      ...theme.typography.h6,
      color: theme.palette.greyBlue[700],
    },
    accordionDetails: {
      display: 'block',
      padding: '0px',
      marginTop: theme.spacing(3),
    },
    printTableWrapper: {
      boxShadow: 'none',
      borderRadius: '0px',
    },
    printTable: {
      border: `2px solid ${theme.palette.grey[300]}`,
    },
    printTableHead: {
      padding: theme.spacing(2),
      lineHeight: '22px',
      fontWeight: 500,
    },
    textCell: {
      ...theme.typography.body1,
    },
    noContentMessage: {
      maxWidth: 'none',
      border: '1px solid rgba(0, 0, 0, .125)',
      borderRadius: theme.spacing(0.5),
    },
    hidden: {
      display: 'none',
    },
  };
});

const StyledTableCell = withStyles((theme) => {
  return {
    root: {
      color: theme.palette.greyBlue[700],
      padding: theme.spacing(1.5, 0.625),
      '&:first-child': {
        paddingLeft: theme.spacing(2),
      },
      '&:nth-child(2)': {
        paddingRight: theme.spacing(2),
      },
      minHeight: theme.spacing(3),
    },
  };
})(TableCell);

const StyledTableRow = withStyles((theme) => {
  return {
    root: {
      border: `2px solid ${theme.palette.grey[300]}`,
    },
  };
})(TableRow);

const HistoryAction = ({ printJob, readableText }) => {
  const classes = useStyles();

  return (
    <>
      <div
        className={classes.hidden}
        id={`print-again-button-label-${printJob.pk}`}
      >
        Print pages <span className="noTranslate">{readableText}</span>
      </div>
      <IconTooltip id="print-again" title="Print Again">
        <IconButton
          onClick={() => uploaderPdfToIframe(printJob.pk)}
          aria-labelledby={`print-again-button-label-${printJob.pk}`}
          className={classes.actionIconButton}
        >
          <Print />
        </IconButton>
      </IconTooltip>
    </>
  );
};

const QueueAction = ({
  printJob,
  onDelete = () => {},
  index,
  readableText,
}) => {
  const classes = useStyles();

  return (
    <>
      <div
        className={classes.hidden}
        id={`remove-pages-button-label-${printJob.id}`}
      >
        Remove pages <span className="noTranslate">{readableText}</span> from
        print queue
      </div>
      <IconTooltip id="remove" title="Remove">
        <IconButton
          aria-labelledby={`remove-pages-button-label-${printJob.id}`}
          onClick={() => onDelete(printJob, index)}
          className={classes.actionIconButton}
        >
          <Delete />
        </IconButton>
      </IconTooltip>
    </>
  );
};

const CollapseButton = ({ open, handleClick }) => {
  const classes = useStyles();

  return (
    <IconButton
      aria-label={
        open
          ? 'Collapse to hide multiple pages printed'
          : 'Expand to show multiple pages printed'
      }
      aria-expanded={open}
      aria-hidden={false}
      className={classes.actionIconButton}
      onClick={handleClick}
    >
      {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
    </IconButton>
  );
};

const PrintJobTableRow = ({
  index,
  printJob,
  printData,
  onDelete,
  totalPages,
}) => {
  const numberOfPages = printJob?.pages?.length;

  const pagesPercent = getPrintPercentForPrintPages(numberOfPages, totalPages);

  const pageRanges = getPageRangesForPrintJob(
    printJob?.pages,
    printJob.pageNumbers,
  );

  const { displayText, readableText, shouldTranslate } = getReadableText(
    printJob,
    pageRanges,
  );

  const classes = useStyles();

  const [open, setOpen] = useState(false);

  return (
    <>
      <StyledTableRow key={printJob.id}>
        <StyledTableCell
          className={
            shouldTranslate
              ? classes.textCell
              : `${classes.textCell} noTranslate`
          }
          scope="row"
        >
          {displayText}
        </StyledTableCell>
        <StyledTableCell className={`${classes.textCell} noTranslate`}>
          {numberOfPages} {pagesPercent && ` (${pagesPercent}%)`}
        </StyledTableCell>
        <TableCell className={classes.actionCell}>
          {printData.action(printJob, readableText, index, onDelete)}
        </TableCell>
        <TableCell className={classes.actionCell}>
          {pageRanges.size > 1 && (
            <CollapseButton open={open} handleClick={() => setOpen(!open)} />
          )}
        </TableCell>
      </StyledTableRow>

      {open &&
        Array.from(pageRanges.keys()).map((range) => {
          const rangeNumberOfPages = pageRanges.get(range);
          const rangePercent = getPrintPercentForPrintPages(
            rangeNumberOfPages,
            totalPages,
          );

          return (
            <TableRow key={range}>
              <StyledTableCell scope="row" className="noTranslate">
                {range}
              </StyledTableCell>
              <StyledTableCell className="noTranslate">
                {rangeNumberOfPages} ({rangePercent}%)
              </StyledTableCell>
              <StyledTableCell />
              <StyledTableCell />
            </TableRow>
          );
        })}
    </>
  );
};

const printDataMap = {
  history: {
    id: 'print-history-content',
    title: 'Print History',
    shouldBeOpenByDefault: false,
    actionCellName: 'Print',
    action: (printJob, readableText) => (
      <HistoryAction printJob={printJob} readableText={readableText} />
    ),
    emptyStateTitle: 'No pages printed',
    emptyStateContent:
      'No pages have been printed to a PDF. Once printed to a PDF, page ranges will show here.',
  },
  queue: {
    id: 'print-queue-content',
    title: 'Print Queue',
    shouldBeOpenByDefault: true,
    actionCellName: '',
    action: (printJob, readableText, index, onDelete) => (
      <QueueAction
        printJob={printJob}
        onDelete={onDelete}
        index={index}
        readableText={readableText}
      />
    ),
    emptyStateTitle: 'No pages selected.',
    emptyStateContent: 'Add pages to the print queue to print them to a PDF.',
  },
};

const PrintAccordion = ({
  printJobsList,
  accordionName,
  totalPages,
  onDeleteClick = () => {},
  ...props
}) => {
  const classes = useStyles();

  const printData = printDataMap[accordionName];

  const itemsListRef = useRef(null);
  const accordionSummaryRef = useRef(null);

  const [processedPrintJobsList, setProcessedPrintJobsList] =
    useState(printJobsList);

  useEffect(() => {
    if (accordionName === 'history') {
      setProcessedPrintJobsList(sortPrintJobs(printJobsList));
    } else {
      // "queue" printJobsList is already sorted
      setProcessedPrintJobsList(printJobsList);
    }
  }, [printJobsList, accordionName]);

  const onDelete = (printJob, index) => {
    if (itemsListRef.current.children[index + 1]) {
      const newRow = itemsListRef.current.children[index + 1];
      newRow.querySelector('button').focus();
    } else if (itemsListRef.current.children[index - 1]) {
      const newRow = itemsListRef.current.children[index - 1];
      newRow.querySelector('button').focus();
    } else {
      accordionSummaryRef.current.focus();
    }

    onDeleteClick(printJob);
  };

  const [expanded, setExpanded] = useState(printData.shouldBeOpenByDefault);
  const showTable = Boolean(processedPrintJobsList?.length) && expanded;

  return (
    <div className={classes.root} {...props}>
      <Accordion
        elevation={0}
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
      >
        <h3
          aria-controls={`${printData.id}-content`}
          id={`${printData.id}-header`}
        >
          <AccordionSummary
            ref={accordionSummaryRef}
            classes={{
              root: classes.accordionSummaryRoot,
              content: classes.accordionSummaryContent,
              expandIcon: classes.accordionIcon,
            }}
            expandIcon={<ExpandMore />}
            IconButtonProps={{ disabled: null }} // fixes an accessibility violation
            id={`${printData.id}-summary`}
            aria-label={`Click to ${expanded ? 'hide ' : 'display '}${
              printData.title
            } items.`}
          >
            <span className={classes.accordionTitle}>{printData.title}</span>
          </AccordionSummary>
        </h3>

        <AccordionDetails className={classes.accordionDetails}>
          {showTable ? (
            <TableContainer
              className={classes.printTableWrapper}
              component={Paper}
            >
              <Table
                aria-label={`Table showing ${printData.title} items`}
                className={classes.printTable}
              >
                <TableHead className={classes.printTableHead} role="rowgroup">
                  <StyledTableRow>
                    <StyledTableCell>Pages</StyledTableCell>
                    <StyledTableCell>Total Pages</StyledTableCell>
                    <StyledTableCell colSpan={2}>Actions</StyledTableCell>
                  </StyledTableRow>
                </TableHead>

                <TableBody ref={itemsListRef} role="rowgroup">
                  {processedPrintJobsList.map((printJob, index) => {
                    return (
                      <PrintJobTableRow
                        index={index}
                        key={printJob.id}
                        printJob={printJob}
                        printData={printData}
                        onDelete={onDelete}
                        totalPages={totalPages}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <NoContentMessage
              className={classes.noContentMessage}
              title={printData.emptyStateTitle}
              content={printData.emptyStateContent}
            />
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default PrintAccordion;

PrintAccordion.propTypes = {
  printJobsList: PropTypes.arrayOf(PropTypes.object).isRequired,
  accordionName: PropTypes.oneOf(['history', 'queue']).isRequired,
  totalPages: PropTypes.number.isRequired,
  onDeleteClick: PropTypes.func,
};
